import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, Building2, Calendar, User, MessageSquare, RefreshCw, Trash2, LogOut, Image as ImageIcon } from 'lucide-react';

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const navigate = useNavigate();

  const fetchSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submissions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch submissions');
      }
      const data = await response.json();
      // Sort by timestamp, newest first
      const sortedData = data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setSubmissions(sortedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const submissionDate = new Date(timestamp);
    const diffMs = now - submissionDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <MessageSquare className="text-[#FF6B6B]" size={32} />
                Contact Form Submissions
              </h1>
              <p className="text-gray-400 mt-1">Manage and view all contact inquiries</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin/portfolio')}
                className="flex items-center gap-2 px-4 py-2 bg-amber-400 text-black rounded-lg hover:bg-amber-300 transition-colors font-semibold"
                data-testid="open-portfolio-editor"
              >
                <ImageIcon size={18} />
                Edit Portfolio
              </button>
              <button
                onClick={fetchSubmissions}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-[#4ECDC4] text-white rounded-lg hover:bg-[#3AB8AD] transition-colors disabled:opacity-50"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-sm text-gray-400">Total Submissions</div>
              <div className="text-2xl font-bold text-white mt-1">{submissions.length}</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-sm text-gray-400">Last 24 Hours</div>
              <div className="text-2xl font-bold text-[#4ECDC4] mt-1">
                {submissions.filter(s => new Date() - new Date(s.timestamp) < 86400000).length}
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-sm text-gray-400">This Week</div>
              <div className="text-2xl font-bold text-[#FFD700] mt-1">
                {submissions.filter(s => new Date() - new Date(s.timestamp) < 604800000).length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-[#FF6B6B]" size={48} />
          </div>
        )}

        {error && (
          <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 text-red-200">
            <p className="font-semibold">Error loading submissions</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {!loading && !error && submissions.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="mx-auto text-gray-600 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-400">No submissions yet</h3>
            <p className="text-gray-500 mt-2">Contact form submissions will appear here</p>
          </div>
        )}

        {!loading && !error && submissions.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {submissions.map((submission) => (
              <div
                key={submission.id}
                className="bg-gray-800 rounded-lg border border-gray-700 hover:border-[#FF6B6B] transition-all duration-300 overflow-hidden cursor-pointer hover:shadow-xl hover:shadow-[#FF6B6B]/20"
                onClick={() => setSelectedSubmission(submission)}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-full flex items-center justify-center">
                        <User size={24} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{submission.name}</h3>
                        <p className="text-sm text-gray-400">{getTimeAgo(submission.timestamp)}</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 bg-gray-900 px-2 py-1 rounded">
                      #{submission.id.slice(0, 8)}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail size={16} className="text-[#4ECDC4]" />
                      <a href={`mailto:${submission.email}`} className="text-gray-300 hover:text-[#4ECDC4]">
                        {submission.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone size={16} className="text-[#FFD700]" />
                      <a href={`tel:${submission.phone}`} className="text-gray-300 hover:text-[#FFD700]">
                        {submission.phone}
                      </a>
                    </div>
                    {submission.company && (
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 size={16} className="text-[#FF6B6B]" />
                        <span className="text-gray-300">{submission.company}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={16} className="text-gray-500" />
                      <span className="text-gray-400">{formatDate(submission.timestamp)}</span>
                    </div>
                  </div>

                  {/* Message Preview */}
                  <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                    <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Message</div>
                    <p className="text-gray-300 text-sm line-clamp-3">{submission.message}</p>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex gap-2">
                    <a
                      href={`mailto:${submission.email}?subject=Re: Your Inquiry&body=Hi ${submission.name},%0D%0A%0D%0AThank you for reaching out.%0D%0A%0D%0A`}
                      className="flex-1 px-4 py-2 bg-[#4ECDC4] text-white text-sm font-medium rounded-lg hover:bg-[#3AB8AD] transition-colors text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Reply via Email
                    </a>
                    <a
                      href={`tel:${submission.phone}`}
                      className="px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Call
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedSubmission(null)}
        >
          <div
            className="bg-gray-800 rounded-xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedSubmission.name}</h2>
                  <p className="text-sm text-gray-400 mt-1">Submitted {getTimeAgo(selectedSubmission.timestamp)}</p>
                </div>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              {/* Full Details */}
              <div className="space-y-4">
                <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                  <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Email</div>
                  <a href={`mailto:${selectedSubmission.email}`} className="text-[#4ECDC4] hover:underline">
                    {selectedSubmission.email}
                  </a>
                </div>

                <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                  <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Phone</div>
                  <a href={`tel:${selectedSubmission.phone}`} className="text-[#FFD700] hover:underline">
                    {selectedSubmission.phone}
                  </a>
                </div>

                {selectedSubmission.company && (
                  <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                    <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Company</div>
                    <p className="text-white">{selectedSubmission.company}</p>
                  </div>
                )}

                <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                  <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Submission Date & Time</div>
                  <p className="text-white">{formatDate(selectedSubmission.timestamp)}</p>
                </div>

                <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                  <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Full Message</div>
                  <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{selectedSubmission.message}</p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <a
                    href={`mailto:${selectedSubmission.email}?subject=Re: Your Inquiry&body=Hi ${selectedSubmission.name},%0D%0A%0D%0AThank you for reaching out.%0D%0A%0D%0A`}
                    className="flex-1 px-6 py-3 bg-[#4ECDC4] text-white font-medium rounded-lg hover:bg-[#3AB8AD] transition-colors text-center"
                  >
                    Reply via Email
                  </a>
                  <a
                    href={`tel:${selectedSubmission.phone}`}
                    className="px-6 py-3 bg-[#FFD700] text-black font-medium rounded-lg hover:bg-[#E5C200] transition-colors"
                  >
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
